// src/utils/domHelpers.ts

export function escapeHtml(value: string): string {
	const element = document.createElement("span");
	element.textContent = value;
	return element.innerHTML;
}

/**
 * Creates a new DOM element and optionally appends it to a parent.
 *
 * @param type - The HTML tag name of the element to create (e.g., 'div', 'button').
 * @param options - Optional configuration for the element.
 * @param options.className - CSS class name(s) to assign.
 * @param options.id - ID attribute for the element.
 * @param options.textContent - Text content for the element.
 * @param options.innerHTML - HTML content for the element.
 * @param options.attributes - Key-value pairs of attributes to set.
 * @param options.style - Key-value pairs of CSS styles to apply.
 * @param parent - The parent element to append the new element to (optional).
 * @returns The newly created HTMLElement.
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
	type: K,
	options?: {
		className?: string;
		id?: string;
		textContent?: string;
		innerHTML?: string;
		attributes?: Record<string, string>;
		style?: Partial<CSSStyleDeclaration>;
	},
	parent?: HTMLElement | null,
): HTMLElementTagNameMap[K] {
	const element = document.createElement(type);

	if (options?.className) {
		// Handle multiple classes separated by space
		options.className.split(" ").forEach((cls) => {
			if (cls) element.classList.add(cls);
		});
	}
	if (options?.id) element.id = options.id;
	if (options?.textContent) element.textContent = options.textContent;
	if (options?.innerHTML) element.innerHTML = options.innerHTML;

	if (options?.attributes) {
		Object.entries(options.attributes).forEach(([key, value]) => {
			if (value != null) {
				element.setAttribute(key, value);
			}
		});
	}

	if (options?.style) {
		Object.assign(element.style, options.style);
	}

	if (parent) {
		parent.appendChild(element);
	}

	return element;
}

/**
 * Safely gets an element by its ID. Throws an error if not found.
 * @param id The ID of the element.
 * @returns The HTMLElement.
 * @throws {Error} If the element with the specified ID is not found.
 */
export function getElementByIdOrThrow(id: string): HTMLElement {
	const element = document.getElementById(id);
	if (!element) {
		throw new Error(`Element with ID "${id}" not found.`);
	}
	return element;
}

/**
 * Safely gets an element by its ID. Returns null if not found.
 * @param id The ID of the element.
 * @returns The HTMLElement or null.
 */
export function getElementByIdSafe(id: string): HTMLElement | null {
	return document.getElementById(id);
}

/**
 * Retrieves the value of a query parameter from the current URL.
 * @param name - The name of the query parameter.
 * @returns The value of the query parameter, or null if it doesn't exist.
 */
export function getQueryParam(name: string): string | null {
	const urlParams = new URLSearchParams(window.location.search);
	return urlParams.get(name);
}

/**
 * Retrieves the slug from the current URL path at a specific position.
 * Positions are counted from the end (1 = last part, 2 = second to last, etc.).
 * Example: /dashboard/entry/123 -> getSlug(1) returns '123', getSlug(2) returns 'entry'
 *
 * @param position - The position of the slug in the URL path (1-based from the end).
 * @returns The slug string at the specified position, or an empty string if not found.
 */
export function getSlug(position: number): string {
	if (position < 1) return "";
	try {
		const pathParts = window.location.pathname
			.split("/")
			.filter((part) => part.length > 0); // Filter out empty strings
		const index = pathParts.length - position;
		return pathParts[index] || ""; // Return empty string if index is out of bounds
	} catch (e) {
		console.error("Error getting slug:", e);
		return "";
	}
}

/**
 * Adjusts the bottom padding of the body to prevent content from being hidden behind a fixed footer.
 * Assumes the footer container has the ID 'button-parent'.
 */
export function adjustBodyPaddingForFooter(): void {
	const footerSection = getElementByIdSafe("button-parent"); // ID used in original JS
	if (
		footerSection &&
		window.getComputedStyle(footerSection).position === "fixed"
	) {
		const footerHeight = footerSection.offsetHeight;
		document.body.style.paddingBottom = `${footerHeight}px`;
	} else {
		document.body.style.paddingBottom = "0px"; // Reset if footer is not fixed or not found
	}
}

/**
 * Enables a button, making it clickable and visually active.
 * @param buttonId - The ID of the button element.
 */
export function enableButton(buttonId: string = "submitButton"): void {
	const button = getElementByIdSafe(buttonId) as HTMLButtonElement | null;
	if (button) {
		button.classList.remove("opacity-25", "disabledButton"); // Classes from original JS
		button.disabled = false;
	}
}

/**
 * Disables a button, making it unclickable and visually inactive.
 * @param buttonId - The ID of the button element.
 */
export function disableButton(buttonId: string = "submitButton"): void {
	const button = getElementByIdSafe(buttonId) as HTMLButtonElement | null;
	if (button) {
		button.classList.add("opacity-25", "disabledButton"); // Classes from original JS
		button.disabled = true;
	}
}

/**
 * Handles scrolling to a URL anchor/hash, waiting for the element to be
 * rendered if necessary.
 * @param {string} [selector] - Optional. A CSS selector for the container where content is rendered.
 *                               Observing a specific container is more performant than observing document.body.
 * @param {number} [timeout=5000] - Optional. Maximum time in ms to wait for the element.
 */
export function handleAnchorScrollOnLoad(
	selector: string = "body",
	timeout: number = 5000,
): void {
	const { hash } = window.location;
	if (!hash) {
		return;
	}

	const targetId = hash.substring(1);

	const scrollToTarget = () => {
		const element = document.getElementById(targetId);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
			return true;
		}
		return false;
	};

	// If the element exists when the function runs, scroll and exit.
	if (scrollToTarget()) {
		return;
	}

	// If not, observe the DOM for changes.
	const container = document.querySelector(selector);
	if (!container) {
		console.error(
			`Anchor scroll failed: container with selector "${selector}" not found.`,
		);
		return;
	}

	const observer = new MutationObserver((_mutations, obs) => {
		if (scrollToTarget()) {
			obs.disconnect(); // Clean up the observer once the element is found.
		}
	});

	observer.observe(container, {
		childList: true,
		subtree: true,
	});

	// Disconnect the observer after a timeout to prevent memory leaks if the element never appears.
	setTimeout(() => observer.disconnect(), timeout);
}

/**
 * Clear all common page containers to prevent content from previous pages bleeding through
 * This should be called before rendering a new page to ensure clean slate
 */
export function clearPageContainers(): void {
	// Remove dynamically injected content-block styles to prevent unbounded <head> growth
	document.head
		.querySelectorAll("style[data-content-block-style]")
		.forEach((el) => el.remove());

	const containerIds = [
		"hero",
		"header",
		"content",
		"footer",
		"prepend-content",
		"pagination-parent", // Clear pagination to prevent duplication
	];

	containerIds.forEach((id) => {
		const element = document.getElementById(id);
		if (element) {
			element.innerHTML = "";
		}
	});
}

/**
 * Sets the document title.
 * @param title - The title to set.
 */
export function setPageTitle(title: string): void {
	document.title = title;
}
